import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import { useTagsQuery } from '@/hooks/useTags'

interface Tag {
  id: string
  name: string
  color: string
}

const TAG_COLORS = ['#6b7280', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899']

export function TagBadge({ tag, onRemove }: { tag: Tag; onRemove?: () => void }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: tag.color }}
    >
      {tag.name}
      {onRemove && (
        <button onClick={onRemove} className="ml-0.5 hover:opacity-70">
          &times;
        </button>
      )}
    </span>
  )
}

export function TagPicker({ urlId, existingTags }: { urlId: string; existingTags: Tag[] }) {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#6b7280')

  const { data: allTags = [] } = useTagsQuery()

  const addTagMutation = useMutation({
    mutationFn: (tagId: string) => apiRequest(`/api/urls/${urlId}/tags/${tagId}`, 'POST'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['url-tags', urlId] })
      queryClient.invalidateQueries({ queryKey: ['urls'] })
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to add tag', 'error')
    },
  })

  const removeTagMutation = useMutation({
    mutationFn: (tagId: string) => apiRequest(`/api/urls/${urlId}/tags/${tagId}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['url-tags', urlId] })
      queryClient.invalidateQueries({ queryKey: ['urls'] })
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to remove tag', 'error')
    },
  })

  const createTagMutation = useMutation({
    mutationFn: () => apiRequest('/api/tags', 'POST', { name: newTagName, color: newTagColor }),
    onSuccess: () => {
      setNewTagName('')
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      showToast('Tag created')
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to create tag', 'error')
    },
  })

  const existingIds = new Set(existingTags.map(t => t.id))
  const availableTags = allTags.filter(t => !existingIds.has(t.id))

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1">
        {existingTags.map(tag => (
          <TagBadge
            key={tag.id}
            tag={tag}
            onRemove={() => removeTagMutation.mutate(tag.id)}
          />
        ))}
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center px-1.5 py-0.5 text-[#4c669a] hover:text-[#1754cf] hover:bg-[#1754cf]/10 rounded text-xs"
          title="Add tag"
        >
          <span className="material-symbols-outlined text-[14px]">add</span>
        </button>
      </div>

      {open && (
        <div className="absolute z-40 top-full left-0 mt-1 bg-white dark:bg-[#1c2230] border border-[#e7ebf3] dark:border-white/10 rounded-lg shadow-lg p-3 min-w-[200px]">
          {availableTags.length > 0 && (
            <div className="mb-2">
              <p className="text-xs text-[#4c669a] mb-1">Existing tags</p>
              <div className="flex flex-wrap gap-1">
                {availableTags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => { addTagMutation.mutate(tag.id); setOpen(false) }}
                    className="hover:opacity-80"
                  >
                    <TagBadge tag={tag} />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="border-t border-[#e7ebf3] dark:border-white/10 pt-2">
            <p className="text-xs text-[#4c669a] mb-1">Create new</p>
            <div className="flex gap-1">
              <input
                type="text"
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
                placeholder="Tag name"
                maxLength={50}
                className="flex-1 px-2 py-1 text-xs bg-white dark:bg-white/5 border border-[#e7ebf3] dark:border-white/10 rounded text-[#0d121b] dark:text-white"
                onKeyDown={e => { if (e.key === 'Enter' && newTagName.trim()) createTagMutation.mutate() }}
              />
              <button
                onClick={() => newTagName.trim() && createTagMutation.mutate()}
                disabled={!newTagName.trim()}
                className="px-2 py-1 text-xs font-bold text-white bg-[#1754cf] rounded hover:opacity-90 disabled:opacity-50"
              >
                Add
              </button>
            </div>
            <div className="flex gap-1 mt-1.5">
              {TAG_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setNewTagColor(c)}
                  className={`w-4 h-4 rounded-full border-2 ${newTagColor === c ? 'border-[#0d121b] dark:border-white' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
